import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";

import { useSelector, useDispatch } from "react-redux";
import GenericEventList from "./GenericEventList";
import AddCalendarEventForm from "./AddCalendarEventForm";
import {
  createCelebrationThunk,
  fetchProposedCelebrationsThunk,
  fetchCelebrationsThunk,
  deleteCelebrationThunk,
} from "../redux/academicCalendarSlice";
import FlatEventListSelector from "./FlatEventListSelector";
import userStore from "../../../zustand/userStore";
import { setStepIndex, startTour} from "../../../redux/globalJoyrideSlice"
import { getCelebrationsTourSteps } from "../../../tour/academicCalenderTourSteps";

export default function CelebrationsCalendar({ onBackToCalendar, onClose, setActiveTab, setTourStage,
  setAwarenessSubTab,
}) {
  const userData = userStore((state) => state.user);
  const dispatch = useDispatch();
  const master = useSelector((s) => s.academicCalendar.masterCelebrations);
  const list = useSelector((s) => s.academicCalendar.celebrations);

  const [subTab, setSubTab] = useState(
    () => sessionStorage.getItem("celebrations.subTab") || "select"
  );
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  useEffect(() => {
    if (subTab === "select") {
      dispatch(fetchProposedCelebrationsThunk({ user_id: userData.id }));
    } else if (subTab === "list") {
      dispatch(fetchCelebrationsThunk({ user_id: userData.id }));
    }
  }, [subTab, dispatch]);
  useEffect(() => {
    sessionStorage.setItem("celebrations.subTab", subTab);
  }, [subTab]);

  const groupedCelebrations = useMemo(() => {
    if (!master || !Array.isArray(master)) return {};

    // Modified this part to always group under "Celebrations"
    return {
      Celebrations: master.map((celebration) => ({
        ...celebration,
        name: celebration.details,
      })),
    };
  }, [master]);
  const handleDeleteCelebration = (id) => {
    if (window.confirm("Are you sure you want to delete this celebration?")) {
      dispatch(deleteCelebrationThunk(id));
    }
  };
  return (
    <div className="px-5 mx-5">
      <div
        id="celebration-add-custom-anchor"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "1px",
          height: "1px",
          pointerEvents: "none",
        }}
      />
      <div className="d-flex mb-2">
        <button className="btn btn-primary me-2" onClick={onBackToCalendar}>
          Back
        </button>
        <button
          className={`btn me-2 ${subTab === "select" ? "btn-primary" : "btn-light"
            }`}
          data-tour="celebrations-proposed-tab"
          onClick={() => {
            setSubTab("select");
            setShowForm(false);
            setEditItem(null);
            dispatch(setStepIndex(1));
          }}
        >
          Proposed Celebrations
        </button>
        <button
          className={`btn me-2 ${subTab === "list" ? "btn-primary" : "btn-light"
            }`}
          data-tour="celebration-list"
          onClick={() => {
            setSubTab("list");
            setShowForm(false);
            setEditItem(null);
          }}
        >
          Celebration List
        </button>
        {userData?.roles !== "user" && (
          <button
            className={`btn ${subTab === "custom" ? "btn-primary" : "btn-light"
              }`}
            onClick={() => {
              setSubTab("custom");
              setShowForm(true);
              setEditItem(null);
            }}
          >
            Add Celebration
          </button>
        )}
      </div>

      {subTab === "select" && (
        <div data-tour="celebrations-select-list">
          <FlatEventListSelector
            userData={userData}
            title="Select Celebrations"
            categories={groupedCelebrations}
            alreadySubmittedIds={list.map((item) => Number(item.celebration_id))}
            onBulkSubmit={async (selected) => {
              try {
                for (const item of selected) {
                  const payload = {
                    school_id: 1,
                    user_id: userData.id,
                    start_date: item.start_date,
                    end_date: item.end_date,
                    details: item.name || item.details,
                    celebration_name:
                      item.celebration_name || "General Celebration",
                    celebration_id: item.id,
                  };
                  await dispatch(createCelebrationThunk(payload)).unwrap();
                }
                alert("Selected celebrations submitted successfully!");
                setSubTab("list");
                // Start confirmation tour: show post-submit confirmation step
                dispatch(
                  startTour({
                    tourName: "celebration-confirm-tour",
                    steps: getCelebrationsTourSteps({
                      dispatch,
                      setCelebrationSubTab: setSubTab,
                      setShowAddCelebrationForm: setShowForm,
                      setActiveTab,
                      setTourStage,
                      setAwarenessSubTab,
                    }),
                  })
                );
              } catch (err) {
                console.error("Submission failed:", err);
                alert("Failed to submit selected celebrations.");
              }
            }}
          />
        </div>
      )}

      {subTab === "list" && (
        <div data-tour="celebration-list-container">
          <GenericEventList
            itemType="celebration"
            title="Celebration"
            data={list}
            onEdit={(item) => {
              setEditItem({
                ...item,
                start_date: moment(item.start_date).format("YYYY-MM-DD"),
                end_date: moment(item.end_date).format("YYYY-MM-DD"),
              });
              setSubTab("custom");
              setShowForm(true);
            }}
            onDelete={handleDeleteCelebration}
          />
        </div>
      )}

      {(subTab === "custom" || showForm) && (
        <AddCalendarEventForm
          itemType="celebration"
          editItem={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
            setSubTab("list");
          }}
        />
      )}
    </div>
  );
}
